import type { ReactNode } from 'react';

import type { VariantProp } from '../../common/colors';
import type { SpacingValue } from '../../common/sizing';
import type { HardwareIconName } from '../HardwareIcon/HardwareIcon';
import type { IconName } from '../Icon/Icon';
import type { ManufacturerIconName } from '../ManufacturerIcon/ManufacturerIcon';
import type { ProductIconName } from '../ProductIcon/ProductIcon';
import type { ProviderIconName } from '../ProviderIcon/ProviderIcon';
import type { ServiceIconName } from '../ServiceIcon/ServiceIcon';
import { FramedIcon } from '../FramedIcon/FramedIcon';
import { HardwareIcon, HardwareIconValues } from '../HardwareIcon/HardwareIcon';
import { Icon, IconNames, Icons } from '../Icon/Icon';
import {
  ManufacturerIcon,
  ManufacturerIconNames,
  ManufacturerIcons,
} from '../ManufacturerIcon/ManufacturerIcon';
import { ProductIcon, ProductIconNames, ProductIcons } from '../ProductIcon/ProductIcon';
import { ProviderIcon, ProviderIconNames, ProviderIcons } from '../ProviderIcon/ProviderIcon';
import { ServiceIcon, ServiceIconNames, ServiceIcons } from '../ServiceIcon/ServiceIcon';

export type DynamicIconName =
  | HardwareIconName
  | IconName
  | ManufacturerIconName
  | ProductIconName
  | ProviderIconName
  | ServiceIconName;

export const DynamicIconNameValues: DynamicIconName[] = [
  ...HardwareIconValues,
  ...IconNames,
  ...ManufacturerIconNames,
  ...ProductIconNames,
  ...ProviderIconNames,
  ...ServiceIconNames,
];

/** Default resolution order when one name exists in multiple icon sets. */
export const DEFAULT_DYNAMIC_ICON_PRIORITY = [
  'hardware',
  'manufacturer',
  'provider',
  'service',
  'framed',
  'product',
] as const;

export type DynamicIconKind = (typeof DEFAULT_DYNAMIC_ICON_PRIORITY)[number];

function resolveEffectivePriority(priority?: DynamicIconKind[]): DynamicIconKind[] {
  return [...new Set([...(priority ?? []), ...DEFAULT_DYNAMIC_ICON_PRIORITY])];
}

export type DynamicIconProps = {
  icon: DynamicIconName;
  size?: SpacingValue;
  variant?: VariantProp;
  priority?: DynamicIconKind[];
};

type DynamicIconForwardedProps = Omit<DynamicIconProps, 'icon' | 'size' | 'variant' | 'priority'>;

type DynamicIconBranchProps = Pick<DynamicIconProps, 'icon' | 'variant'> &
  DynamicIconForwardedProps & {
    size: SpacingValue;
  };

function isHardwareIconName(icon: DynamicIconName): icon is HardwareIconName {
  return HardwareIconValues.some((name) => name === icon);
}

function isIconName(icon: DynamicIconName): icon is IconName {
  return icon in Icons;
}

function isManufacturerIconName(icon: DynamicIconName): icon is ManufacturerIconName {
  return icon in ManufacturerIcons;
}

function isProviderIconName(icon: DynamicIconName): icon is ProviderIconName {
  return icon in ProviderIcons;
}

function isServiceIconName(icon: DynamicIconName): icon is ServiceIconName {
  return icon in ServiceIcons;
}

function isProductIconName(icon: DynamicIconName): icon is ProductIconName {
  return icon in ProductIcons;
}

function branchPropsWithoutVariant(
  props: DynamicIconBranchProps,
): Omit<DynamicIconBranchProps, 'variant'> {
  const { variant, ...rest } = props;
  return rest;
}

function kindMatchesBranch(kind: DynamicIconKind, icon: DynamicIconName): boolean {
  switch (kind) {
    case 'hardware':
      return isHardwareIconName(icon);
    case 'manufacturer':
      return isManufacturerIconName(icon);
    case 'provider':
      return isProviderIconName(icon);
    case 'service':
      return isServiceIconName(icon);
    case 'framed':
      return isIconName(icon);
    case 'product':
      return isProductIconName(icon);
  }
  return false;
}

function DynamicIconHardware(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, variant, ...remaining } = props;
  if (!isHardwareIconName(icon)) {
    return null;
  }
  if (size < 20) {
    return <Icon icon={icon} size={size} variant={variant} {...remaining} />;
  }
  return <HardwareIcon icon={icon} size={size} {...remaining} />;
}

function DynamicIconManufacturer(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, ...remaining } = branchPropsWithoutVariant(props);
  if (!isManufacturerIconName(icon)) {
    return null;
  }
  return <ManufacturerIcon icon={icon} size={size} {...remaining} />;
}

function DynamicIconProvider(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, ...remaining } = branchPropsWithoutVariant(props);
  if (!isProviderIconName(icon)) {
    return null;
  }
  return <ProviderIcon icon={icon} size={size} {...remaining} />;
}

function DynamicIconService(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, ...remaining } = branchPropsWithoutVariant(props);
  if (!isServiceIconName(icon)) {
    return null;
  }
  return <ServiceIcon icon={icon} size={size} {...remaining} />;
}

function DynamicIconFramed(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, variant, ...remaining } = props;
  if (!isIconName(icon)) {
    return null;
  }
  if (size < 20) {
    return <Icon icon={icon} size={size} variant={variant} {...remaining} />;
  }
  return <FramedIcon icon={icon} size={size} variant={variant} {...remaining} />;
}

function DynamicIconProduct(props: DynamicIconBranchProps): ReactNode {
  const { icon, size, ...remaining } = branchPropsWithoutVariant(props);
  if (!isProductIconName(icon)) {
    return null;
  }
  return <ProductIcon icon={icon} size={size} {...remaining} />;
}

function DynamicIconKindMatch({
  kind,
  ...branchProps
}: { kind: DynamicIconKind } & DynamicIconBranchProps): ReactNode {
  switch (kind) {
    case 'hardware':
      return <DynamicIconHardware {...branchProps} />;
    case 'manufacturer':
      return <DynamicIconManufacturer {...branchProps} />;
    case 'provider':
      return <DynamicIconProvider {...branchProps} />;
    case 'service':
      return <DynamicIconService {...branchProps} />;
    case 'framed':
      return <DynamicIconFramed {...branchProps} />;
    case 'product':
      return <DynamicIconProduct {...branchProps} />;
  }
}

export function DynamicIcon({
  icon,
  size = 16,
  variant,
  priority,
  ...remaining
}: DynamicIconProps) {
  const order = resolveEffectivePriority(priority);
  const branchProps: DynamicIconBranchProps = { icon, size, variant, ...remaining };
  const kind = order.find((k) => kindMatchesBranch(k, icon));
  if (kind === undefined) {
    return null;
  }
  return <DynamicIconKindMatch kind={kind} {...branchProps} />;
}
